import React, { useContext, useEffect, useRef, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ApiContext } from '@api/api';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import { AuditEntityTypeEnum, EntityTypeEnum, PermissionTypesEnum, VisitorRecordDetailOutDTO } from '@api/ringil-api.v2.tsx';
import useNavigation from '@hooks/useNavigation.tsx';
import { isNullOrUndefined } from '@utils/utils.tsx';
import Heading from '@components/ringil3/Heading/Heading.tsx';
import LabelsTag from '@components/framework/tags/LabelsTag.tsx';
import CreatedBadge from '@components/ringil3/Badges/CreatedBadge.tsx';
import UpdatedBadge from '@components/ringil3/Badges/UpdatedBadge.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import DateTimeBadge from '@components/ringil3/Badges/DateTimeBadge.tsx';
import Tabs, { TabProps } from '@components/ringil3/RingilTabs/Tabs.tsx';
import SvgUsers from '@icons/users-inverted.svg?react';
import SvgInfo from '@icons/info.svg?react';
import EntityAuditLog, { EntityAuditLogHandle } from '@components/RingilComponents/audit/EntityAuditLog.tsx';
import SecuredArea from '@components/security/SecuredArea';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import Visitdetail from '@app/pages/timeslots/visit/VisitDetail.tsx';
import { Ringil3BadgeRow } from '@components/ringil3/styles.tsx';
import WorkflowStateTag from '@components/workflow/WorkflowStateTag.tsx';
import MoreButton from '@components/RingilComponents/MoreButton/MoreButton.tsx';
import { useVisitorSidebar } from '@app/pages/timeslots/sidebars/VisitorSidebar.tsx';

interface ComponentProps {
  visitId: string;
}

const Visit: React.FC<ComponentProps> = ({ visitId }) => {
  const { tr } = useTranslationLgs();
  const navigation = useNavigation();
  const { hasPermission } = useContext(AuthenticatedUserContext);
  const { VisitorSidebar, setVisible: setVisibleVisitorSidebar, visible: visibleVisitorSidebar } = useVisitorSidebar();

  const {
    getVisitorRecord,
    archiveVisitorRecord,
  } = useContext(ApiContext);
  const [data, setData] = useState<VisitorRecordDetailOutDTO>();
  const [activeTab, setActiveTab] = useState(0);
  const auditLogRef = useRef<EntityAuditLogHandle>(null);
  // Audit/history is gated on its own dedicated read permission — the 2.0 audit endpoint 403s without it.
  const showAudit = useSecuredFlag('visit-detail-audit', {
    kind: 'visibility',
    description: 'Visitor record change history / audit log',
    permissions: [PermissionTypesEnum.PERMISSION_VISITOR_RECORD_DETAIL_AUDITLOG_READ],
  });

  const tabsConfig: TabProps[] = [
    {
      label: tr(`VisitDetail.tabTitleVisitor`, `Visitor`),
      active: activeTab === 0,
      icon: <SvgUsers />,
    },
    ...(showAudit.granted
      ? [
          {
            label: tr(`VisitDetail.tabTitleHistory`, `History`),
            active: activeTab === 1,
            icon: <SvgInfo />,
          },
        ]
      : []),
  ];

  const load = () => {
    getVisitorRecord(visitId, data => {
      setData(data);
      auditLogRef.current?.reload();
    });
  };

  useEffect(() => {
    load();
  }, [visitId]);

  const editEnabled = hasPermission([PermissionTypesEnum.PERMISSION_TIMESLOT_VISITORS_WRITE]);
  const editStatusEnabled = hasPermission([PermissionTypesEnum.PERMISSION_TIMESLOT_VISITORS_WRITE, PermissionTypesEnum.PERMISSION_TIMESLOT_CHANGE_STATUS]);

  if (isNullOrUndefined(data)) return <></>;

  return (
    <>
      <BreadCrumbComponent
        items={[
          {
            label: tr(`Visitors.visitorsTitle`, `Visitors`),
            url: navigation.createNavigationLink(navigation.urlFunctions.createVisitorList()),
          },
          { label: data.applicationId, url: null },
        ]}
      />
      <div className="flex flex-row w-full items-center mb-5 relative">
        <div className="flex flex-row flex-wrap gap-4 md:gap-8 md:items-center">
          <Heading
            title={data.applicationId}
            headingVariant={'h1'}
            labels={
              <LabelsTag
                objectId={data.id}
                objectType={EntityTypeEnum.VISITOR_RECORD}
                resolveLabelsForType={EntityTypeEnum.VISITOR_RECORD}
                disabled={!editEnabled}
                onChange={() => load()}
              />
            }
            className="max-md:[&+.not-empty]:w-full whitespace-nowrap min-h-12"
          />

          {editEnabled && (
            // MoreButton has no className of its own, so the header's placement lives on a wrapper.
            <div className="absolute top-0 right-0">
              <MoreButton
                sections={[
                  {
                    items: [
                      {
                        id: 'duplicate',
                        label: tr('Visitors.moreActionDuplicate', 'Duplicate'),
                        command: () => {
                          getVisitorRecord(data.id, (responseData) => {
                            setVisibleVisitorSidebar(true);
                          });
                        },
                      },
                      {
                        id: 'archive',
                        label: tr('Visitors.moreActionArchive', 'Archive'),
                        command: () => {
                          archiveVisitorRecord(
                            data.id,
                            { isArchived: true },
                            () => {
                              load()
                            },
                          );
                        },
                      },
                    ],
                  },
                ]}
              />
            </div>
          )}
        </div>
      </div>

      <Ringil3BadgeRow>
        <CreatedBadge createdAt={data.createdDate} createdBy={data.createdBy} timezoneId={'Europe/Prague'}/>
        <UpdatedBadge updatedAt={data.lastModifiedDate} updatedBy={data.lastModifiedBy} timezoneId={'Europe/Prague'}/>
        <DateTimeBadge label={tr('Visit.badgeArrival', 'Arrival')} dateTime={data.actualArrivalDate} timezoneId={'Europe/Prague'}/>
        <DateTimeBadge label={tr('Visit.badgeDeparture', 'Departure')} dateTime={data.actualDepartureDate} timezoneId={'Europe/Prague'}/>
      </Ringil3BadgeRow>

      <Ringil3BadgeRow>
        <WorkflowStateTag
          state={data.state}
          entityId={data.id}
          entityType={EntityTypeEnum.VISITOR_RECORD}
          onReload={() => {load()}}
          readOnly={!editStatusEnabled}
        />
      </Ringil3BadgeRow>

      <Tabs
        tabs={tabsConfig}
        onTabChange={setActiveTab}
      />

      {activeTab === 0 && <Visitdetail data={data} loadData={load} />}
      {activeTab === 1 && (
        <SecuredArea id="visit-detail-audit" label="Audit log" description="History of all changes made to the visit" visible={showAudit}>
          <EntityAuditLog ref={auditLogRef} entityType={AuditEntityTypeEnum.VISITOR_RECORD} entityId={data.id} hideHeader />
        </SecuredArea>
      )}

      {visibleVisitorSidebar && (
        <VisitorSidebar
          visible={visibleVisitorSidebar}
          setVisible={setVisibleVisitorSidebar}
          item={{
            duplicate: true,
            record: data,
          }}
          onContinue={(id) => {
            setVisibleVisitorSidebar(false);
            navigation.navigate(navigation.urlFunctions.createVisitDetail(id));
          }}
        />
      )}
    </>
  );
};

export default Visit;
